<template>
  <div class="as-a-coach-pending-inspection-requests-table">
    <!-- Top Search Bar -->
    <div v-if="items.length > 0" class="search-bar-wrapper">
      <SearchBar
        :label="appliedFilters.length > 0 ? 'More Filters' : 'Filter'"
        :length="appliedFilters.length"
        ghostText="Search"
        :filterBtn="true"
        :chipText="appliedFilterLabels"
        :valueSearch="searchQuery"
        @onClick="handleSearchBarClick"
        @onSearch="handleSearch"
        @removeFilter="handleRemoveFilter"
        class="flex-grow-1 custom-search-bar"
        removeColumnClass
      />
    </div>

    <!-- Table -->
    <div class="table-container no-border">
      <table v-if="paginatedItems.length > 0" class="inspection-table">
        <thead>
          <tr>
            <th class="header-cell pl-6" width="22%">Requested by</th>
            <th class="header-cell" width="24%">For level / Requested on</th>
            <th class="header-cell" width="30%">For responsibility</th>
            <th class="header-cell" width="12%">Status</th>
            <th class="header-cell pr-6" width="12%"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedItems" :key="item.id" class="table-row">
            <!-- Requested by (employee card) -->
            <td class="cell pl-6 py-4">
              <EmployeeCard
                v-bind="getEmployeeProps(item.employee)"
                :showImportanceSelector="false"
              />
            </td>

            <!-- For level / Requested on -->
            <td class="cell py-4">
              <div class="level-date-container">
                <MinimalisticReadinessLevel :level="getRequestedLevel(item)" />
                <span class="requested-on-text">{{ formatRequestedOn(item) }}</span>
                <span v-if="item.isExpedited" class="expedited-badge-chip">
                  Expedited
                </span>
              </div>
            </td>

            <!-- For responsibility (responsibility + assignment dropdown) -->
            <td class="cell py-4">
              <ResponsibilityAssignmentDropDown
                v-bind="getAssignmentProps(item)"
                :show_codes="true"
                class="full-width-dropdown"
              />
            </td>

            <!-- Status -->
            <td class="cell py-4">
              <InspectionRequestStatusBadge :status="item.status || 'pending'" />
            </td>

            <!-- Row actions + chatter -->
            <td class="cell pr-6 py-4">
              <div class="action-cell-container">
                <StandardButton
                  type="magenta"
                  variant="primary"
                  size="small"
                  label="Accept"
                  aria-label="Accept inspection request"
                  @click="handleAccept(item)"
                />
                <StandardButton
                  type="red"
                  variant="outline"
                  size="small"
                  label="Decline"
                  aria-label="Decline inspection request"
                  @click="handleDecline(item)"
                />
                <ChatterIcon
                  :unread="item.unreadMessages || 0"
                  @onClick="handleChatterClick(item)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty states -->
      <div v-else class="empty-state-section">
        <NoRecordFound
          v-if="items.length > 0"
          showSpecificMessage="No requests found"
          description="Your filter or search did not match any requests.<br/>Please try again."
          :showClearAction="true"
          clearLabel="Clear search"
          :shouldShowAddAction="false"
          :showActiveCounter="false"
          :noBorder="true"
          @onClear="clearAllFilters"
        />
        <NoRecordFound
          v-else
          showSpecificMessage="No pending inspection requests for you"
          description="Employees need to request inspections and specify the inspector they want."
          :showClearAction="false"
          :shouldShowAddAction="false"
          :showActiveCounter="false"
          :noBorder="true"
        />
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="showPaginationSection" class="pagination-section">
      <ExPagination :pagination="pagination" @onRequest="handlePaginationRequest" />
    </div>

    <!-- Filter Modal -->
    <SearchModal
      :dialog="showFilterModal"
      filterType="Inspection"
      :filterGroups="filterGroups"
      :statusOptions="[]"
      :groupsItems="[]"
      accountType="coach"
      :defaultSelected="appliedFilterLabels"
      @back="showFilterModal = false"
      @checkedItemsChanged="handleFiltersChanged"
    />

    <!-- Decline required-reason modal -->
    <ReasonModal
      v-if="declineModalOpen"
      :showDialog="declineModalOpen"
      :title="'Decline inspection request'"
      :description="'Please provide a reason for declining this inspection request.'"
      :placeHolder="'Explain why you are declining this request.'"
      :btnLabel="'Decline'"
      :btnDanger="true"
      :value="declineReason"
      @input="(val) => (declineReason = val)"
      @onAction="handleDeclineReasonAction"
      @onClose="closeDeclineModal"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import EmployeeCard from '@components/organizations/employees/EmployeeCard/EmployeeCard.vue';
import ResponsibilityAssignmentDropDown from '@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue';
import MinimalisticReadinessLevel from '@components/MinimalisticReadinessLevel/MinimalisticReadinessLevel.vue';
import StandardButton from '@components/StandardButton/StandardButton.vue';
import ChatterIcon from '@components/ChatterIcon/ChatterIcon.vue';
import SearchBar from '@components/SearchBar/SearchBar.vue';
import SearchModal from '@components/SearchBar/SearchModal.vue';
import ExPagination from '@components/DataTable/ExPagination.vue';
import NoRecordFound from '@components/NoRecordFound/NoRecordFound.vue';
import InspectionRequestStatusBadge from '@components/InspectionRequestStatusBadge/InspectionRequestStatusBadge.vue';
import ReasonModal from '@components/ReasonModal/ReasonModal.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['accept', 'decline', 'chatter-click']);

const searchQuery = ref('');
const appliedFilters = ref([]);
const showFilterModal = ref(false);
const currentPage = ref(1);
const ITEMS_PER_PAGE = 10;

// Decline modal state
const declineModalOpen = ref(false);
const declineReason = ref('');
const decliningItem = ref(null);

const VALID_LEVELS = ['apprentice', 'professional', 'coach', 'master'];

const normalizeLevel = (level) => {
  if (!level) return 'apprentice';
  return String(level).toLowerCase();
};

const getRequestedLevel = (item) => {
  const level = item.requestedLevel || item.requested_level || item.targetLevel || item.badge_target_level;
  const normalized = normalizeLevel(level);
  return VALID_LEVELS.includes(normalized) ? normalized : 'apprentice';
};

const getRequestedOnValue = (item) =>
  item.requestedOn || item.requested_on || item.createdOn || item.created_at || item.createdAt;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const formatRequestedOn = (item) => {
  const value = getRequestedOnValue(item);
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  // UTC getters: date-only ISO strings ("2026-08-19") parse as UTC midnight,
  // so local-timezone getters could roll the day back — use UTC consistently.
  return `On ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
};

const getEmployeeProps = (employee) => {
  if (!employee) return {};
  return {
    firstName: employee.firstName || employee.first_name,
    lastName: employee.lastName || employee.last_name,
    position: employee.position || employee.title,
    photoUrl: employee.photoUrl || employee.avatar,
  };
};

const getAssignmentProps = (item) => {
  if (!item) return {};
  const assignment = item.assignment || {};
  return {
    responsibilityCode: assignment.code || assignment.responsibilityCode || item.code || '',
    responsibilityName: assignment.name || assignment.responsibilityName || item.name || '',
    roleCode: assignment.roleCode || '',
    roleName: assignment.roleName || '',
    positionCode: assignment.positionCode || '',
    positionName: assignment.positionName || '',
    responsibilityImportance: assignment.responsibilityImportance || 0,
    roleImportance: assignment.roleImportance || 0,
    positionImportance: assignment.positionImportance || 0,
    positionCommitment: assignment.positionCommitment || 'Primary',
  };
};

const filterGroups = [
  {
    label: 'Type',
    items: [
      { label: 'Expedited', value: 'Expedited' },
      { label: 'Standard', value: 'Standard' },
    ],
  },
];

const filteredItems = computed(() => {
  let result = props.items;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter((item) => {
      const firstName = item.employee?.first_name || item.employee?.firstName || '';
      const lastName = item.employee?.last_name || item.employee?.lastName || '';
      const employeeName = `${firstName} ${lastName}`.toLowerCase();
      const responsibilityName = (item.assignment?.name || item.assignment?.responsibilityName || '').toLowerCase();
      const responsibilityCode = (item.assignment?.code || item.assignment?.responsibilityCode || '').toLowerCase();
      return (
        employeeName.includes(query) ||
        responsibilityName.includes(query) ||
        responsibilityCode.includes(query)
      );
    });
  }

  if (appliedFilters.value.length > 0) {
    result = result.filter((item) =>
      appliedFilters.value.every((filter) => {
        if (filter.labelType === 'Type') {
          if (filter.value === 'Expedited') return item.isExpedited;
          if (filter.value === 'Standard') return !item.isExpedited;
        }
        return true;
      })
    );
  }

  return result;
});

const pagination = computed(() => ({
  page: currentPage.value,
  total: filteredItems.value.length,
  perPage: ITEMS_PER_PAGE,
}));

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;
  return filteredItems.value.slice(start, end);
});

const showPaginationSection = computed(
  () => filteredItems.value.length > ITEMS_PER_PAGE
);

const handlePaginationRequest = (data) => {
  currentPage.value = data.page;
};

const handleSearchBarClick = (type) => {
  if (type === 'filter') {
    showFilterModal.value = true;
  }
};

const handleSearch = (value) => {
  searchQuery.value = value;
  currentPage.value = 1;
};

const handleFiltersChanged = (filters) => {
  // Drop radio "No Filter" selections (value: null) so they don't count as an active filter.
  appliedFilters.value = (filters || []).filter((f) => f && f.value);
  showFilterModal.value = false;
  currentPage.value = 1;
};

const appliedFilterLabels = computed(() =>
  appliedFilters.value.map((f) => f.value)
);

const handleRemoveFilter = (removedValue) => {
  appliedFilters.value = appliedFilters.value.filter((f) => f.value !== removedValue);
  currentPage.value = 1;
};

const clearAllFilters = () => {
  searchQuery.value = '';
  appliedFilters.value = [];
  currentPage.value = 1;
};

const handleAccept = (item) => {
  emit('accept', item);
};

const handleDecline = (item) => {
  decliningItem.value = item;
  declineReason.value = '';
  declineModalOpen.value = true;
};

const handleDeclineReasonAction = () => {
  if (!decliningItem.value || !declineReason.value.trim()) {
    return;
  }
  emit('decline', { item: decliningItem.value, reason: declineReason.value.trim() });
  closeDeclineModal();
};

const closeDeclineModal = () => {
  declineModalOpen.value = false;
  declineReason.value = '';
  decliningItem.value = null;
};

const handleChatterClick = (item) => {
  emit('chatter-click', item);
};

defineExpose({
  handleSearch,
  handleFiltersChanged,
  handleRemoveFilter,
  handlePaginationRequest,
  handleAccept,
  handleDecline,
  handleDeclineReasonAction,
  closeDeclineModal,
  handleChatterClick,
  filteredItems,
  formatRequestedOn,
  declineReason,
  declineModalOpen,
});
</script>

<style scoped>
.as-a-coach-pending-inspection-requests-table {
  background: #ffffff;
  font-family: Inter, sans-serif;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
}

.search-bar-wrapper {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.flex-grow-1 {
  flex-grow: 1;
}

.table-container {
  width: 100%;
  overflow: visible;
  border-radius: 8px;
}

.table-container.no-border {
  border: none !important;
  box-shadow: none !important;
}

.inspection-table {
  width: 100%;
  border-collapse: collapse;
}

.header-cell {
  background-color: #f9fafb;
  height: 44px;
  padding: 12px 8px;
  font-size: 12px;
  color: #667085;
  text-align: left;
  font-weight: 500;
  border-bottom: 1px solid #eaecf0;
  white-space: nowrap;
}

.cell {
  padding: 16px 8px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.pl-6 { padding-left: 20px !important; }
.pr-6 { padding-right: 20px !important; }
.py-4 { padding-top: 16px !important; padding-bottom: 16px !important; }

.table-row:last-child .cell {
  border-bottom: none;
}

.level-date-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.requested-on-text {
  color: #667085;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

.expedited-badge-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fdf2fa;
  color: #c11574;
  border: 1px solid #fcceee;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-cell-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.empty-state-section {
  width: 100%;
}

.pagination-section {
  padding: 12px 16px 16px 16px;
  border-top: 1px solid #eaecf0;
}

:deep(.full-width-dropdown) {
  width: 100% !important;
}

:deep(.rad__trigger-btn) {
  flex-grow: 1 !important;
  width: 100% !important;
}

.custom-search-bar :deep(.filter-input-search) {
  width: 400px !important;
}
</style>
